/**
 * StickyAddToCart Web Component
 * Displays a sticky add-to-cart button that appears when the main button is out of view
 */
class StickyAddToCart extends HTMLElement {
  static BOTTOM_THRESHOLD = 200;
  static VISIBILITY_CLASS = 'is-visible';

  constructor() {
    super();
    this.state = {
      isVisible: false,
      mainButton: null,
      headerHeight: 0
    };
    this.observers = {
      mainButton: null
    };
    this.scrollHandler = null;
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Initialize the component
   */
  initialize() {
    this.findElements();
    
    if (!this.state.mainButton) {
      console.warn('StickyAddToCart: Main button not found');
      return;
    }

    this.updateHeaderHeight();
    this.setupObservers();
    this.attachEventListeners();
  }

  /**
   * Find and store references to DOM elements
   */
  findElements() {
    const mainButtonId = this.querySelector('[data-main-button-id]')?.dataset.mainButtonId;
    
    if (mainButtonId) {
      this.state.mainButton = document.getElementById(mainButtonId);
    }
  }

  /**
   * Get the current header height from CSS variable
   */
  updateHeaderHeight() {
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height');
    this.state.headerHeight = parseInt(cssValue) || 0;
  }

  /**
   * Setup intersection observers for main button
   */
  setupObservers() {
    this.setupMainButtonObserver();
  }

  /**
   * Setup observer for main add-to-cart button
   */
  setupMainButtonObserver() {
    if (!this.state.mainButton) return;

    this.observers.mainButton = new IntersectionObserver(
      (entries) => this.handleMainButtonIntersection(entries),
      {
        rootMargin: `-${this.state.headerHeight}px 0px 0px 0px`,
        threshold: 0
      }
    );

    this.observers.mainButton.observe(this.state.mainButton);
  }

  /**
   * Handle main button intersection changes
   */
  handleMainButtonIntersection(entries) {
    entries.forEach((entry) => {
      const buttonRect = entry.boundingClientRect;
      const isButtonHidden = this.isButtonHidden(entry, buttonRect);

      if (isButtonHidden && !this.isNearPageBottom()) {
        this.show();
      } else if (!isButtonHidden) {
        this.hide();
      }
    });
  }

  /**
   * Check if button is hidden (above viewport or under header)
   */
  isButtonHidden(entry, buttonRect) {
    return !entry.isIntersecting || 
           buttonRect.top < this.state.headerHeight || 
           buttonRect.bottom < this.state.headerHeight;
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    const button = this.querySelector('.sticky-add-to-cart__button');
    if (button) {
      button.addEventListener('click', (e) => this.handleButtonClick(e));
    }

    this.scrollHandler = () => this.handleScroll();
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  /**
   * Handle scroll events to show/hide sticky bar
   */
  handleScroll() {
    if (!this.state.mainButton) return;

    if (this.isNearPageBottom()) {
      this.hide();
      return;
    }

    const mainButtonRect = this.state.mainButton.getBoundingClientRect();
    const isButtonOutOfView = this.isButtonOutOfView(mainButtonRect);

    if (isButtonOutOfView) {
      this.show();
    } else if (this.isButtonFullyVisible(mainButtonRect)) {
      this.hide();
    }
  }

  /**
   * Check if button is out of view (scrolled past)
   */
  isButtonOutOfView(buttonRect) {
    return buttonRect.top < this.state.headerHeight || 
           buttonRect.bottom < this.state.headerHeight || 
           buttonRect.top < 0;
  }

  /**
   * Check if button is fully visible in viewport
   */
  isButtonFullyVisible(buttonRect) {
    return buttonRect.top >= this.state.headerHeight && 
           buttonRect.bottom <= window.innerHeight;
  }

  /**
   * Check if user is near the bottom of the page
   */
  isNearPageBottom() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    
    return distanceFromBottom < StickyAddToCart.BOTTOM_THRESHOLD;
  }

  /**
   * Handle sticky button click
   */
  handleButtonClick(event) {
    event.preventDefault();
    
    if (this.state.mainButton && !this.state.mainButton.disabled) {
      this.state.mainButton.click();
    }
  }

  /**
   * Show the sticky add-to-cart bar
   */
  show() {
    if (this.state.isVisible) return;
    
    if (this.isNearPageBottom()) return;
    if (this.state.mainButton) {
      const mainButtonRect = this.state.mainButton.getBoundingClientRect();
      if (this.isButtonFullyVisible(mainButtonRect)) return;
    }
    
    this.state.isVisible = true;
    this.classList.add(StickyAddToCart.VISIBILITY_CLASS);
  }

  /**
   * Hide the sticky add-to-cart bar
   */
  hide() {
    if (!this.state.isVisible) return;
    
    this.state.isVisible = false;
    this.classList.remove(StickyAddToCart.VISIBILITY_CLASS);
  }

  /**
   * Cleanup observers and event listeners
   */
  cleanup() {
    Object.values(this.observers).forEach(observer => {
      if (observer) observer.disconnect();
    });

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);